import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '@jpmuitk/dropdown';
import { FormField } from '@jpmuitk/form-field';
import { AriaAnnouncerProvider } from '@jpmuitk/aria-announcer';
import { RiGlobalLine } from "react-icons/ri";
import { FaChevronDown } from "react-icons/fa";
import { styled } from '@material-ui/core';
import { ADA_COMPLIANT_GRAY } from '../ThemeConstants';

const languageOptions = ['English', 'Deutsch', 'Nederlands'];
const ACCOUNT_REGION_US = 'US';
const ENGLISH_LANGUAGE_CODE = 'en';

export default function LanguageContainer({ accountRegion }) {
  const { i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const isUsProfile = accountRegion === ACCOUNT_REGION_US;

  const languageMap = {
    English: 'en',
    Deutsch: 'de',
    Nederlands: 'nl'
  };

  const getBaseLanguage = (lang) => {
    if (typeof lang !== 'string' || !lang) return '';
    return lang.split('-')[0];
  };

  const currentLanguageTitle = Object.keys(languageMap).find(
    key => languageMap[key] === getBaseLanguage(selectedLanguage)
  );

  useEffect(() => {
    if (isUsProfile) {
      if (getBaseLanguage(i18n.language) !== ENGLISH_LANGUAGE_CODE) {
        i18n.changeLanguage(ENGLISH_LANGUAGE_CODE).catch((error) => {
          console.error('Error changing language:', error);
        });
      }
      localStorage.setItem('language', ENGLISH_LANGUAGE_CODE);
      setSelectedLanguage(ENGLISH_LANGUAGE_CODE);
      return;
    }

    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage && storedLanguage !== i18n.language) {
      i18n.changeLanguage(storedLanguage).then(() => {
        setSelectedLanguage(storedLanguage);
      }).catch((error) => {
        console.error('Error changing language:', error);
      });
    }
  }, [i18n, isUsProfile]);

  const handleLanguageChange = (selectedLanguage) => {
    if (isUsProfile) {
      return;
    }

    const languageCode = languageMap[selectedLanguage];
    if (languageCode && i18n.language !== languageCode) {
      i18n.changeLanguage(languageCode).then(() => {
        localStorage.setItem('language', languageCode);
        setSelectedLanguage(languageCode);
        window.dispatchEvent(new Event('storage'));
      }).catch((error) => {
        console.error('Error changing language:', error);
      });
    } else {
      console.error('Invalid language selected:', selectedLanguage);
    }
  };

  const CustomFormField = styled(FormField)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    '& [class*="activationIndicator"]': {
      borderBottom: 'none !important'
    },
  }));

  return (
    <AriaAnnouncerProvider>
      <div style={{ width: 120, marginRight: '10px' }}>
        <CustomFormField label={
          <span style={{ color: ADA_COMPLIANT_GRAY }}>
          Language
        </span>} variant={'transparent'} inputProps={{
          style: {
            borderBottom: 'none',
          },
        }}>
          <Dropdown
            density={'medium'}
            initialSelectedItem={currentLanguageTitle}
            disabled={isUsProfile}
            source={isUsProfile ? ['English'] : languageOptions}
            onSelect={(event, selectedItem) => {
              handleLanguageChange(selectedItem);
            }}
            selectedItem={currentLanguageTitle}
          >
            {({ DropdownButtonProps, itemToString, selectedItem }) => (
              <div
                {...DropdownButtonProps}
                aria-disabled={isUsProfile}
                onClick={isUsProfile ? undefined : DropdownButtonProps.onClick}
                onKeyDown={isUsProfile ? undefined : DropdownButtonProps.onKeyDown}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 6px',
                  cursor: isUsProfile ? 'not-allowed' : 'pointer',
                  opacity: isUsProfile ? 0.65 : 1,
                }}
              >
                <RiGlobalLine style={{ marginRight: 8, fontSize: '1.2em', flexShrink: 0 }} />
                <span style={{ flexGrow: 1, textAlign: 'center' }}>{itemToString(selectedItem)}</span>
                <FaChevronDown style={{ marginLeft: 8, fontSize: '0.8em', flexShrink: 0 }}
                               aria-label={"Toggle dropdown"}/>
              </div>
            )}
          </Dropdown>
        </CustomFormField>
      </div>
    </AriaAnnouncerProvider>
  );
}
